import React from 'react'

import {flatMap, intersection, isNil, uniq} from 'es-toolkit'
import invariant from 'invariant'
import {useNavigate} from 'react-router'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {fatalError} from '../../redux/features/core'
import {openConfirmDeleteModal} from '../../redux/features/modal'

import useSearch from '../../../../commons/src/hooks/use-search'
import {isBefore} from '../../../../commons/src/services/date'
import useSelectedIds from '../../hooks/use-selected-ids'
import useSortAttributes, {SortDefinition} from '../../hooks/use-sort-attributes'
import {formatDateTime, formatDensity, formatPercentage, formatPrice} from '../../libs/formatter'
import * as priceApi from '../../services/api/price'
import {goToCreatePricing, goToEditPricing, goToProviderInfos} from '../../services/navigation'
import {createPricingSearch} from '../../services/search'

import {Price, PriceId, PricingRowContent, PricingTab, ProviderInfo} from '../../types'

import checkedIcon from '../../../../commons/src/assets/icons/checked.svg'
import cloneIcon from '../../../../commons/src/assets/icons/clone.svg'
import deleteIcon from '../../../../commons/src/assets/icons/delete.svg'
import editIcon from '../../../../commons/src/assets/icons/edit.svg'
import tableSearchIcon from '../../../../commons/src/assets/icons/table-search.svg'

import ActionBar from '../../../../commons/src/components/action-bar/ActionBar'
import CheckboxField from '../../../../commons/src/components/checkbox-field'
import FilterBar from '../../../../commons/src/components/filter-bar'
import Icon from '../../../../commons/src/components/icon'
import LinkButton from '../../../../commons/src/components/link-button'
import Paragraph from '../../../../commons/src/components/paragraph/'
import SearchInputField from '../../../../commons/src/components/search-input-field'
import Stack from '../../../../commons/src/components/stack'
import ActionHeaderLayout from '../../components/action-header-layout'
import ActionHeader from '../../components/action-header-layout/action-header'
import StaggeredValues from '../../components/staggered-values/StaggeredValues'
import Table from '../../components/table'
import TableCell from '../../components/table/TableCell'
import TableCellGroup from '../../components/table/TableCellGroup'
import TableHeaderCell from '../../components/table/TableHeaderCell'
import TableHeaderCellGroup from '../../components/table/TableHeaderCellGroup'
import TableRow from '../../components/table/TableRow'

type Props = {
  prices: Price[]
  providerInfosByPriceId: Record<PriceId, ProviderInfo[]>
  isLoading: boolean
  reload: () => void
}

const SORT_ATTRIBUTES: SortDefinition<PricingRowContent> = {
  name: {
    asc: ({price: {name: a}}, {price: {name: b}}) => a.localeCompare(b),
    desc: ({price: {name: a}}, {price: {name: b}}) => b.localeCompare(a),
  },
  updatedAt: {
    asc: ({price: {updatedAt: a}}, {price: {updatedAt: b}}) =>
      typeof a !== 'undefined' && typeof b !== 'undefined' ? (isBefore(a, b) ? -1 : 1) : -1,
    desc: ({price: {updatedAt: a}}, {price: {updatedAt: b}}) =>
      typeof a !== 'undefined' && typeof b !== 'undefined' ? (isBefore(a, b) ? 1 : -1) : -1,
  },
  linkedConfigs: {
    asc: ({providerInfos: a}, {providerInfos: b}) => a.length - b.length,
    desc: ({providerInfos: a}, {providerInfos: b}) => b.length - a.length,
  },
}

const getNumberOfLinkedConfigs = (
  providerInfosByPriceId: Record<PriceId, ProviderInfo[]>,
  selectedIds: PriceId[]
) => {
  const uniqueProviderInfoIds = uniq(flatMap(selectedIds, id => providerInfosByPriceId[id] || []))

  return uniqueProviderInfoIds.length
}

const Pricings3DPrintingPartial = ({prices, providerInfosByPriceId, isLoading, reload}: Props) => {
  const dispatch = useAppDispatch()
  const provider = useAppSelector(state => state.core.provider)

  // This will never happen when user is authenticated. Just a safety check for TS.
  invariant(provider, 'Provider is missing')

  const navigate = useNavigate()

  const {toggleId, toggleAll, isSelected, isAnySelected, selectedIds} = useSelectedIds<PriceId>(
    () => prices.map(price => price.id)
  )

  const tableRows = React.useMemo(
    () =>
      prices.map(price => ({
        price,
        providerInfos: providerInfosByPriceId[price.id] || [],
      })),
    [prices, providerInfosByPriceId]
  )

  const {searchResults, setSearchValue, searchValue} = useSearch(tableRows, createPricingSearch)

  const searchedAndFilteredTableRows = React.useMemo(
    () => intersection(searchResults, tableRows),
    [searchResults, tableRows]
  )

  const {
    sortedItems: sortedTableRows,
    sortAttribute,
    sortOrder,
    toggleSortBy,
  } = useSortAttributes(searchedAndFilteredTableRows, SORT_ATTRIBUTES, 'name')

  const renderVolumePrice = (volumePrice: Price['volumePrice']) => {
    if (isNil(volumePrice)) {
      return ''
    }

    if (typeof volumePrice === 'number') {
      return formatPrice(volumePrice, provider.currency)
    }

    return (
      <StaggeredValues
        values={volumePrice.map(({volume, price}) => ({
          left: formatPrice(price, provider.currency),
          right: `${volume} cm³`,
        }))}
      />
    )
  }

  const renderWeightPrice = (weightPrice: NonNullable<Price['weightPrice']>) => {
    if (typeof weightPrice === 'number') {
      return formatPrice(weightPrice, provider.currency)
    }

    return (
      <StaggeredValues
        values={weightPrice.map(({weight, price}) => ({
          left: formatPrice(price, provider.currency),
          right: `${weight} kg`,
        }))}
      />
    )
  }

  const renderRow = ({price, providerInfos: relatedProviderInfos}: PricingRowContent) => (
    <TableRow key={price.id}>
      <TableCell noBorder>
        <ActionBar>
          <CheckboxField checked={isSelected(price.id)} onChange={() => toggleId(price.id)} />
        </ActionBar>
      </TableCell>
      <TableCell strong>{price.name}</TableCell>
      <TableCellGroup>
        <TableCell width={2}>{renderVolumePrice(price.volumePrice)}</TableCell>
        <TableCell width={2}>
          {price.weightPrice && price.density && (
            <>
              {renderWeightPrice(price.weightPrice)}
              {
                <Paragraph minor className="u-no-margin-bottom">
                  ({formatDensity(price.density)})
                </Paragraph>
              }
            </>
          )}
        </TableCell>
        <TableCell width={1}>
          {price.boundingBoxPrice && formatPrice(price.boundingBoxPrice, provider.currency)}
        </TableCell>
        <TableCell width={1}>
          {price.surfacePrice && formatPrice(price.surfacePrice, provider.currency)}
        </TableCell>
      </TableCellGroup>
      <TableCellGroup>
        <TableCell>
          {price.baseUnitPrice && formatPrice(price.baseUnitPrice, provider.currency)}
        </TableCell>
        <TableCell>{price.basePrice && formatPrice(price.basePrice, provider.currency)}</TableCell>
      </TableCellGroup>
      <TableCellGroup>
        <TableCell>
          {price.minimumUnitPrice && formatPrice(price.minimumUnitPrice, provider.currency)}
        </TableCell>
        <TableCell>
          {price.minimumPrice && formatPrice(price.minimumPrice, provider.currency)}
        </TableCell>
      </TableCellGroup>
      <TableCellGroup>
        <TableCell>{price.quantityDiscount && price.quantityDiscount}</TableCell>
        <TableCell>
          {price.quantityDiscountCap && formatPercentage(price.quantityDiscountCap)}
        </TableCell>
      </TableCellGroup>
      <TableCell>{!price.useVolumeWithSupport ? '' : <Icon source={checkedIcon} />}</TableCell>
      <TableCell error={relatedProviderInfos.length === 0}>
        {relatedProviderInfos.length > 0 ? (
          <LinkButton
            icon={<Icon source={tableSearchIcon} />}
            iconAlign="right"
            onClick={() =>
              goToProviderInfos(navigate, {
                initialFilters: {
                  price: [price.id],
                },
              })
            }
          >
            <strong>{relatedProviderInfos.length}</strong>
          </LinkButton>
        ) : (
          <strong>{relatedProviderInfos.length}</strong>
        )}
      </TableCell>
      <TableCell minor>{formatDateTime(price.updatedAt)}</TableCell>
    </TableRow>
  )

  return (
    <ActionHeaderLayout
      header={
        <ActionHeader
          left={
            <LinkButton onClick={() => toggleAll()}>
              {isAnySelected() ? 'Deselect all' : 'Select all'}
            </LinkButton>
          }
          right={
            <>
              <LinkButton
                size="200"
                icon={<Icon source={editIcon} title="Edit" />}
                disabled={selectedIds.length === 0}
                onClick={() =>
                  goToEditPricing(navigate, {
                    pricings: prices.filter(({id}) => selectedIds.includes(id)),
                  })
                }
              />
              <LinkButton
                size="200"
                icon={<Icon source={cloneIcon} title="Clone" />}
                disabled={selectedIds.length === 0 || selectedIds.length > 1}
                onClick={() =>
                  goToCreatePricing(navigate, {
                    activeTab: PricingTab.THREE_D_PRINTING,
                    price: prices.filter(({id}) => selectedIds.includes(id))[0],
                  })
                }
              />
              <LinkButton
                size="200"
                icon={<Icon source={deleteIcon} title="Delete" />}
                disabled={selectedIds.length === 0}
                onClick={() => {
                  dispatch(
                    openConfirmDeleteModal({
                      itemsToDeleteCount: selectedIds.length,
                      linkedConfigsCount: getNumberOfLinkedConfigs(
                        providerInfosByPriceId,
                        selectedIds
                      ),
                      onConfirm: () => {
                        priceApi
                          .removeMultiple(selectedIds)
                          .then(reload)
                          .catch(error => dispatch(fatalError({error})))
                      },
                    })
                  )
                }}
              />
            </>
          }
        />
      }
      filterBar={
        <FilterBar
          searchBar={
            <SearchInputField
              size="90"
              name="pricings-search"
              placeholder="Search by name…"
              value={searchValue}
              onChange={value => {
                setSearchValue(value)
              }}
            />
          }
        />
      }
    >
      <Table
        isLoading={isLoading}
        empty={
          tableRows.length === 0 ? (
            'No pricings have been added yet.'
          ) : (
            <Stack>
              <div>No pricings found</div>
              <LinkButton
                onClick={() => {
                  setSearchValue('')
                }}
              >
                Clear all filters
              </LinkButton>
            </Stack>
          )
        }
        border
        header={
          <TableRow>
            <TableHeaderCell width="28px" noBorder />
            <TableHeaderCell
              width={2}
              sort={sortAttribute === 'name' && sortOrder ? sortOrder : undefined}
              onClick={() => toggleSortBy('name')}
            >
              Name
            </TableHeaderCell>
            <TableHeaderCellGroup width={6} label="Price by" align="right" labelAlign="center">
              <TableHeaderCell width={2}>Volume</TableHeaderCell>
              <TableHeaderCell width={2}>Weight (Density)</TableHeaderCell>
              <TableHeaderCell width={1}>Bounding box</TableHeaderCell>
              <TableHeaderCell width={1}>Surface</TableHeaderCell>
            </TableHeaderCellGroup>
            <TableHeaderCellGroup width={2} align="right" label="Base price" labelAlign="center">
              <TableHeaderCell>per piece</TableHeaderCell>
              <TableHeaderCell>per file</TableHeaderCell>
            </TableHeaderCellGroup>
            <TableHeaderCellGroup width={2} align="right" label="Minimum price" labelAlign="center">
              <TableHeaderCell>per piece</TableHeaderCell>
              <TableHeaderCell>per file</TableHeaderCell>
            </TableHeaderCellGroup>
            <TableHeaderCellGroup
              width={2}
              align="right"
              label="Quantity discount"
              labelAlign="center"
            >
              <TableHeaderCell width={1} align="right">
                exponent
              </TableHeaderCell>
              <TableHeaderCell width={1} align="right">
                cap
              </TableHeaderCell>
            </TableHeaderCellGroup>
            <TableHeaderCell width={1} align="right">
              Use volume with support
            </TableHeaderCell>
            <TableHeaderCell
              width={1}
              align="right"
              sort={sortAttribute === 'linkedConfigs' && sortOrder ? sortOrder : undefined}
              onClick={() => toggleSortBy('linkedConfigs')}
            >
              Linked materials
            </TableHeaderCell>
            <TableHeaderCell
              width={1.5}
              sort={sortAttribute === 'updatedAt' && sortOrder ? sortOrder : undefined}
              onClick={() => toggleSortBy('updatedAt')}
            >
              Last updated
            </TableHeaderCell>
          </TableRow>
        }
      >
        {sortedTableRows.map(renderRow)}
      </Table>
    </ActionHeaderLayout>
  )
}

export default Pricings3DPrintingPartial
